import React, { useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Navbar from './Navbar'
import { useI18n } from '../lib/i18n'

interface HeroMobileProps {
  backgroundImageUrl?: string;
  backgroundVideoUrl?: string;
  logoUrl?: string;
}

export default function HeroMobile({ backgroundImageUrl, backgroundVideoUrl }: HeroMobileProps) {
  const containerRef = useRef<HTMLElement>(null)
  const navigate = useNavigate()
  const { t } = useI18n()
  const [arrivalDate, setArrivalDate] = useState('')
  const [departureDate, setDepartureDate] = useState('')

  const handleSubmit = () => {
    if (arrivalDate && departureDate) {
      navigate(`/buchen?arrivalDate=${arrivalDate}&departureDate=${departureDate}&step=2`)
    } else {
      navigate('/buchen')
    }
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        staggerChildren: 0.15, 
        delayChildren: 0.1 
      } 
    }
  }

  const headlineVariants = {
    hidden: { x: -30, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1, 
      transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } 
    }
  }

  const subtitleVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: { 
      x: 0, 
      opacity: 1, 
      transition: { duration: 0.8, delay: 0.3 } 
    }
  }

  const cardVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.6, ease: "easeOut", delay: 0.4 } 
    }
  }

  const bgVariants = {
    hidden: { clipPath: "inset(0% 0% 100% 0%)", opacity: 0 },
    visible: { 
      clipPath: "inset(0% 0% 0% 0%)", 
      opacity: 1, 
      transition: { duration: 1, ease: "easeInOut", delay: 0.2 } 
    }
  }

  const badgeVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      transition: { type: "spring", stiffness: 200, delay: 1.2 } 
    }
  }

  return (
    <motion.section 
      ref={containerRef}
      className="hero-mobile relative min-h-screen w-full overflow-hidden bg-[#F4F5F7]"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Background */}
      {backgroundImageUrl && (
        <motion.img 
          src={backgroundImageUrl} 
          alt="Background" 
          crossOrigin="anonymous" 
          className="absolute inset-0 w-full h-full object-cover" 
          variants={bgVariants}
        />
      )}
      
      {/* Video Background Support */}
      {backgroundVideoUrl && (
        <motion.video
          autoPlay
          loop
          muted
          playsInline
          crossOrigin="anonymous"
          className="absolute inset-0 w-full h-full object-cover"
          variants={bgVariants}
        >
          <source src={backgroundVideoUrl} type="video/mp4" />
        </motion.video>
      )}

      {/* Navbar */}
      <Navbar />

      {/* Hero Content - Flex Container for automatic spacing */}
      <div className="relative z-10 flex flex-col px-[5vw] pt-[12vh] pb-[2vh]">
        {/* Headline */}
        <motion.h1
          className="headline text-[clamp(1.5rem,5vw,2rem)] font-bold leading-[1.15] text-[#172B4D] tracking-tight mb-3"
          variants={headlineVariants}
        >
          {t.hero.headline}<br />
          {t.hero.headlineLine2}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="subtitle text-[clamp(0.85rem,3vw,1rem)] leading-relaxed text-[#42526E] mb-5"
          variants={subtitleVariants}
        >
          {t.hero.subtitle}<br />
          {t.hero.subtitleLine2}
        </motion.p>

        {/* Booking Form Card */}
        <motion.div
          className="form-card bg-white rounded-xl shadow-lg p-5 flex flex-col gap-4 mx-[-2vw]"
          variants={cardVariants}
        >
          {/* Date Row */}
          <div className="flex gap-3">
            {/* Hinfahrt */}
            <div className="flex-1">
              <label className="block text-xs text-[#172B4D] mb-1 font-medium">{t.hero.arrivalLabel}</label>
              <input
                type="date"
                value={arrivalDate}
                onChange={(e) => setArrivalDate(e.target.value)}
                min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="w-full border border-gray-200 rounded-lg py-2.5 px-2 text-sm text-[#172B4D] bg-white focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
              />
              <p className="text-[10px] text-gray-500 mt-1">{t.hero.bookingNote}</p>
            </div>

            {/* Rückfahrt */}
            <div className="flex-1">
              <label className="block text-xs text-[#172B4D] mb-1 font-medium">{t.hero.departureLabel}</label>
              <input
                type="date"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                min={arrivalDate || new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className="w-full border border-gray-200 rounded-lg py-2.5 px-2 text-sm text-[#172B4D] bg-white focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
              />
            </div>
          </div>

          {/* CTA Button */}
          <motion.button
            onClick={handleSubmit}
            className="w-full bg-[#0052CC] text-white font-bold rounded-full py-3 text-sm shadow-sm"
            whileHover={{
              scale: 1.03,
              backgroundColor: '#0747A6',
              boxShadow: "0px 10px 20px rgba(0, 82, 204, 0.3)"
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2 }}
          >
            {t.hero.ctaButton}
          </motion.button>
        </motion.div>
      </div>

      
    </motion.section>
  )
}